import { useEffect, useMemo, useState } from "react";

import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
} from "V2.0/common/dropdown";

import VoiceAssistantConfigure from "./Configure";
import VoiceAssitantUsageAndStats from "./Dashboard";
import AssistantsBackToHome from "../common/AssistantsBackToHome";
import AssistantIcon from "../common/AssistantIcon";
import AssistantName from "../common/AssistantName";
import AssistantId from "../common/AssistantId";

import WebCall from "../AssistantTest/WebCall";
import { TestVoice } from "V2.0/HomePages/Assistants/Assistant/AssistantTest/TestVoice";
import DemoComponent from "V2.0/components/DemoComponent";
import TabsWithBadge from "V2.0/common/tabsWithBadge";

import {
  AdjustmentsVerticalIcon,
  ChatBubbleLeftEllipsisIcon,
  GlobeAltIcon,
  ListBulletIcon,
  PhoneArrowUpRightIcon,
  PresentationChartBarIcon,
  SparklesIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import { useOutletContext } from "react-router-dom";
import AssistantCoach from "../../AssistantTools/AssistantCoach";
import { GrDocumentTest } from "react-icons/gr";
import EvalRuns from "../../EvalRuns";

enum Tabs {
  Configure = "Configure",
  Dashboard = "Dashboard",
  AssistantCoach = "Assistant Coach",
  EvalRuns = "Automated Testing",
}

const TabIcon = {
  [Tabs.Configure]: AdjustmentsVerticalIcon,
  [Tabs.Dashboard]: PresentationChartBarIcon,
  [Tabs.AssistantCoach]: SparklesIcon,
  [Tabs.EvalRuns]: GrDocumentTest,
};

const TabsRegistry = {
  [Tabs.Configure]: (props) => <VoiceAssistantConfigure {...props} />,
  [Tabs.Dashboard]: (props) => <VoiceAssitantUsageAndStats {...props} />,
  [Tabs.AssistantCoach]: (props) => <AssistantCoach {...props} />,
  [Tabs.EvalRuns]: (props) => <EvalRuns {...props} />,
};

export default function VoiceAssistant(props) {
  const [activeTab, setActiveTab] = useState(Tabs.Configure);
  const {
    assistantType,
    name,
    assistantId,
    workspaceId,
    syncAssistant,
    locked,
    voiceSettings,
    timezoneSettings,
    modelSettings,
    communicationBinding,
    isViewOnly,
    sections,
    configurationSections,
  } = props;
  const { projects, activeProject, projectId } = useOutletContext<{
    projects: [];
    activeProject: object;
    projectId: string;
  }>();

  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [callOpen, setCallOpen] = useState<boolean>(false);
  const [webcallOpen, setWebcallOpen] = useState<boolean>(false);

  const tabs = useMemo(() => {
    console.log({ sections });
    return sections.reduce((acc, tab) => {
      const Icon = TabIcon[tab];
      const isActive = tab === activeTab;

      const tabConfig = {
        name: tab,
        href: "#",
        current: isActive,
        icon: <Icon className="size-5" />,
      };

      return [...acc, tabConfig];
    }, []);
  }, [sections, activeTab]);

  useEffect(() => {
    console.log({ tabs });
  }, [tabs]);

  const fromNumbers = useMemo(() => {
    return communicationBinding.map((binding) => {
      return binding.identifier;
    });
  }, [communicationBinding]);

  return (
    <>
      <div className="flex flex-col h-full w-full">
        <div className="flex flex-col items-start w-full gap-y-2">
          <div className="flex justify-between items-start w-full pt-4 px-4">
            <div className="flex gap-x-2">
              <AssistantsBackToHome />
              <AssistantIcon assistantType={assistantType} />
              <div className="flex flex-col gap-y-2">
                <AssistantName
                  name={name}
                  assistantId={assistantId}
                  workspaceId={workspaceId}
                  syncAssistant={syncAssistant}
                  locked={locked || isViewOnly}
                />
                <AssistantId assistantId={assistantId} />
              </div>
            </div>
            <div>
              <Dropdown>
                <DropdownButton>Test Assistant</DropdownButton>
                <DropdownMenu>
                  <DropdownItem
                    onClick={() => {
                      setCallOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-x-2">
                      <PhoneArrowUpRightIcon className="size-4" /> Phone Call
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      setWebcallOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-x-2">
                      <GlobeAltIcon className="size-4" /> Web Call (Beta)
                    </div>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      setChatOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-x-2">
                      <ChatBubbleLeftEllipsisIcon className="size-4" /> Chat
                    </div>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          <TabsWithBadge
            tabs={tabs}
            className="border-b w-full px-6"
            onTabChange={(e, { tab, index }) => setActiveTab(tab.name)}
          />
        </div>

        <div className="h-full w-full flex flex-col px-2 flex-1 min-h-0 overflow-auto">
          {TabsRegistry[activeTab]({
            ...props,
            locked: locked || isViewOnly,
            configurationSections,
            showPremiumOptions:
              props?.company?.thirdPartyIntegrationVisibility
                ?.premiumModelVisibility,
            enablePremiumOptions:
              props?.company?.thirdPartyIntegrationVisibility
                ?.premiumModelsSelectable,
          })}
        </div>
      </div>
      {chatOpen && (
        <DemoComponent
          open={chatOpen}
          setOpen={setChatOpen}
          projects={projects}
          activeProject={activeProject}
          assistantId={assistantId}
          SidebarTitle="Chat with Assistant"
        />
      )}
      {callOpen && (
        <TestVoice
          voiceSettings={voiceSettings}
          assistantTimezone={timezoneSettings.timezone}
          prompt={modelSettings.promptSettings.rawPrompt}
          fromNumbers={fromNumbers}
          projectId={projectId}
          assistantId={assistantId}
          open={callOpen}
          onClose={() => setCallOpen(false)}
        />
      )}
      {webcallOpen && (
        <WebCall
          assistantId={assistantId}
          workspaceId={workspaceId}
          assistantName={name}
          onClose={() => setWebcallOpen(false)}
        />
      )}
    </>
  );
}
